import { useEffect, useState, useCallback, useContext } from "react";
import { getFunctions } from "firebase/functions";
import UserContext from "../assets/user_context";
import { tracedCallable } from "../utilities/tracedCallable";
import { useThemeMode } from "../theme/ThemeContext";

/**
 * ChatwootWidget - Integrates Chatwoot live chat into T3 Books
 *
 * Features:
 * - Loads Chatwoot SDK dynamically
 * - Syncs authenticated user info to Chatwoot
 * - Uses server-side HMAC for secure identity verification
 * - Includes org context for support agents
 */
export function ChatwootWidget() {
  const { user, userData, org, orgData } = useContext(UserContext);
  const { mode } = useThemeMode();
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [userSynced, setUserSynced] = useState(false);

  const chatwootUrl = import.meta.env.VITE_CHATWOOT_URL;
  const websiteToken = import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN;

  // Sync Firebase user to Chatwoot (with server-side HMAC)
  const syncUserToChatwoot = useCallback(async () => {
    if (!user || !window.$chatwoot || userSynced) return;

    try {
      // Get HMAC hash from Cloud Function (secure!)
      const functions = getFunctions();
      const getIdentityHash = tracedCallable(functions, "getChatwootIdentityHash");
      const result = await getIdentityHash();
      const { identifierHash } = result.data;

      // Build user display name
      const firstName = userData?.firstName || "";
      const lastName = userData?.lastName || "";
      const displayName =
        firstName || lastName
          ? `${firstName} ${lastName}`.trim()
          : user.email?.split("@")[0] || "User";

      // Build custom attributes with org context
      const customAttributes = {
        firebase_uid: user.uid,
      };

      // Add org info if available (helps support agents)
      if (org && orgData) {
        customAttributes.organization_id = org;
        customAttributes.organization_name = orgData.orgName || "Unknown Org";
      }

      window.$chatwoot.setUser(user.uid, {
        email: user.email,
        name: displayName,
        identifier_hash: identifierHash,
        custom_attributes: customAttributes,
      });

      setUserSynced(true);
      console.log("Chatwoot user synced:", displayName);
    } catch (error) {
      // No fallback: Chatwoot enforces identity validation, so a setUser
      // without identifier_hash is rejected anyway. Leaving the contact
      // anonymous is safer than a half-verified identity — the support bot
      // gates data access on a verified identifier.
      console.error("Failed to sync user to Chatwoot:", error);
    }
  }, [user, userData, org, orgData, userSynced]);

  // Set Chatwoot dark mode setting based on app theme
  useEffect(() => {
    window.chatwootSettings = window.chatwootSettings || {};
    window.chatwootSettings.darkMode = mode;
  }, [mode]);

  // Load Chatwoot SDK
  useEffect(() => {
    // Skip if not configured
    if (!chatwootUrl || !websiteToken) return;

    // Set dark mode before SDK loads
    window.chatwootSettings = window.chatwootSettings || {};
    window.chatwootSettings.darkMode = mode;

    // Check if already loaded
    if (window.$chatwoot) {
      setSdkLoaded(true);
      return;
    }

    // Check if script is already in DOM
    if (document.querySelector(`script[src*="${chatwootUrl}"]`)) {
      // Wait for it to load
      const checkLoaded = setInterval(() => {
        if (window.$chatwoot) {
          setSdkLoaded(true);
          clearInterval(checkLoaded);
        }
      }, 100);
      return () => clearInterval(checkLoaded);
    }

    const script = document.createElement("script");
    script.src = `${chatwootUrl}/packs/js/sdk.js`;
    script.defer = true;
    script.async = true;

    script.onload = () => {
      window.chatwootSDK.run({
        websiteToken: websiteToken,
        baseUrl: chatwootUrl,
      });

      // Wait for $chatwoot to be available
      const checkReady = setInterval(() => {
        if (window.$chatwoot) {
          setSdkLoaded(true);
          clearInterval(checkReady);
        }
      }, 100);
    };

    script.onerror = () => {
      console.error("Failed to load Chatwoot SDK");
    };

    document.body.appendChild(script);

    return () => {
      // Don't remove script on unmount - causes issues with re-mounting
    };
  }, [chatwootUrl, websiteToken, mode]);

  // Sync user when SDK is loaded and user is available
  useEffect(() => {
    if (sdkLoaded && user && !userSynced) {
      syncUserToChatwoot();
    }
  }, [sdkLoaded, user, userSynced, syncUserToChatwoot]);

  // Reset sync state when user changes (e.g., logout/login)
  useEffect(() => {
    setUserSynced(false);
  }, [user?.uid]);

  // Reset Chatwoot on logout
  useEffect(() => {
    if (sdkLoaded && !user && window.$chatwoot) {
      window.$chatwoot.reset();
      setUserSynced(false);
    }
  }, [sdkLoaded, user]);

  // Update custom attributes when org changes
  useEffect(() => {
    if (sdkLoaded && user && userSynced && window.$chatwoot && org && orgData) {
      // Update custom attributes with new org info
      window.$chatwoot.setCustomAttributes({
        organization_id: org,
        organization_name: orgData.orgName || "Unknown Org",
      });
    }
  }, [sdkLoaded, user, userSynced, org, orgData]);

  // Don't render if Chatwoot is not configured
  if (!chatwootUrl || !websiteToken) {
    return null;
  }

  return null; // Widget renders itself via the SDK
}

export default ChatwootWidget;
